import { createSlice } from '@reduxjs/toolkit'
import {
  takePlo4SeatThunk,
  takePlo4ActionThunk,
  updatePlo4PlayerSittingStatusThunk,
  updatePlo4BuyInAmountThunk,
  changePlo4SeatThunk,
  getPlo4GameTablesThunk
} from 'redux-thunk/thunk/Plo4GameManagement.thunk'

const initialState = {
  gameData: null,
  gameState: 'idle',
  Plo4GameTables: [],
  currentSeat: null,
  playerActions: [],
  gamePot: 0,
  communityCards: [],
  currentPlayer: null,
  loading: false,
  // Specific loading states for different actions
  takingSeatLoading: false,
  takingActionLoading: false,
  error: null,
  pixi: {
    isAssetsLoaded: false,
    isInitialized: false
  }
}

const Plo4Slice = createSlice({
  name: 'plo4',
  initialState,
  reducers: {
    setGameData: (state, action) => {
      state.gameData = action.payload
    },
    setGameState: (state, action) => {
      state.gameState = action.payload
    },
    setCurrentSeat: (state, action) => {
      state.currentSeat = action.payload
    },
    addPlayerAction: (state, action) => {
      state.playerActions.push(action.payload)
    },
    setGamePot: (state, action) => {
      state.gamePot = action.payload
    },
    setCommunityCards: (state, action) => {
      state.communityCards = action.payload
    },
    setCurrentPlayer: (state, action) => {
      state.currentPlayer = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setPixiAssetsLoaded: (state, action) => {
      state.pixi.isAssetsLoaded = action.payload
    },
    setPixiInitialized: (state, action) => {
      state.pixi.isInitialized = action.payload
    },
    updateTableSeatOccupied: (state, action) => {
      const { tableId, seatsOccupied } = action.payload
      const tableIndex = state.Plo4GameTables.findIndex(table => String(table.id) === String(tableId))
      if (tableIndex !== -1) {
        state.Plo4GameTables[tableIndex].seatsOccupied = seatsOccupied
      }
    }
  },
  extraReducers: (builder) => {
    // Take Plo4 Seat
    builder
      .addCase(takePlo4SeatThunk.pending, (state) => {
        state.takingSeatLoading = true
        state.error = null
      })
      .addCase(takePlo4SeatThunk.fulfilled, (state, action) => {
        state.takingSeatLoading = false
        state.currentSeat = action.payload
      })
      .addCase(takePlo4SeatThunk.rejected, (state, action) => {
        state.takingSeatLoading = false
        state.error = action.error.message
      })

    // Take Plo4 Action
    builder
      .addCase(takePlo4ActionThunk.pending, (state) => {
        state.takingActionLoading = true
        state.error = null
      })
      .addCase(takePlo4ActionThunk.fulfilled, (state, action) => {
        state.takingActionLoading = false
        state.playerActions.push(action.payload)
        // Update pot if action affects it
        if (action.payload.potAmount) {
          state.gamePot = action.payload.potAmount
        }
      })
      .addCase(takePlo4ActionThunk.rejected, (state, action) => {
        state.takingActionLoading = false
        state.error = action.error.message
      })

    // Update Plo4 Player Sitting Status
    builder
      .addCase(updatePlo4PlayerSittingStatusThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePlo4PlayerSittingStatusThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update game data with new sitting status
        if (state.gameData) {
          state.gameData.playerSittingStatus = action.payload
        }
      })
      .addCase(updatePlo4PlayerSittingStatusThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Update Plo4 Buy In Amount
    builder
      .addCase(updatePlo4BuyInAmountThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePlo4BuyInAmountThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update game data with new buy-in amount
        if (state.gameData) {
          state.gameData.buyInAmount = action.payload
        }
      })
      .addCase(updatePlo4BuyInAmountThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Change Plo4 Seat
    builder
      .addCase(changePlo4SeatThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(changePlo4SeatThunk.fulfilled, (state, action) => {
        state.loading = false
        state.currentSeat = action.payload
      })
      .addCase(changePlo4SeatThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Get Plo4 Game Tables
    builder
      .addCase(getPlo4GameTablesThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getPlo4GameTablesThunk.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload?.data ?? action.payload
        // Backend returns { tables: [...], pagination: {...} }
        const tables = Array.isArray(data) ? data : (data?.tables ?? data?.rows ?? [])
        state.Plo4GameTables = tables.map(table => ({
          ...table,
          seatsOccupied: table.seatsOccupied !== undefined
            ? table.seatsOccupied
            : table?.tableSeats?.filter(seat => seat.userId !== null && seat.sittingOut === false).length || 0
        }))
      })
      .addCase(getPlo4GameTablesThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  }
})

export const {
  setGameData,
  setGameState,
  setCurrentSeat,
  addPlayerAction,
  setGamePot,
  setCommunityCards,
  setCurrentPlayer,
  clearError,
  updateTableSeatOccupied,

  // pixi
  setPixiAssetsLoaded,
  setPixiInitialized
} = Plo4Slice.actions

export default Plo4Slice.reducer
